import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { getDomainsRequest } from 'store/domain';
import { ISenderAccount } from 'types/senderAccounts/senderAccount.types';
import * as senderAccountService from '../../services/senderAccountService/senderAccountService';
import {
  createSenderAccountFailure,
  createSenderAccountSuccess,
  deleteSenderAccountFailure,
  deleteSenderAccountSuccess,
  getSenderAccountByIdFailure,
  getSenderAccountByIdSuccess,
  getVerifiedSenderAccountsFailure,
  getVerifiedSenderAccountsSuccess,
  updateSenderAccountFailure,
  updateSenderAccountSuccess,
} from './senderAccounts.action';
import {
  CREATE_SENDER_ACCOUNT_REQUEST,
  CreateSenderAccountRequestAction,
  DELETE_SENDER_ACCOUNT_REQUEST,
  DeleteSenderAccountRequestAction,
  GET_SENDER_ACCOUNT_BY_ID_REQUEST,
  GET_VERIFIED_SENDER_ACCOUNTS_REQUEST,
  GetSenderAccountByIdRequestAction,
  UPDATE_SENDER_ACCOUNT_REQUEST,
  UpdateSenderAccountRequestAction,
} from './senderAccounts.types';

interface SenderAccountResponse {
  senderAccount: ISenderAccount;
}

function* createSenderAccountWorker({
  payload,
}: CreateSenderAccountRequestAction) {
  try {
    const response: SenderAccountResponse = yield call(
      senderAccountService.upsertSenderAccount,
      payload
    );
    yield put(createSenderAccountSuccess(response.senderAccount));
    yield put(getDomainsRequest());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      error.response?.data?.message?.details;
    yield put(
      createSenderAccountFailure(
        errorMessage || 'Failed to create sender account'
      )
    );
  }
}

function* updateSenderAccountWorker({
  payload,
}: UpdateSenderAccountRequestAction) {
  try {
    const response: SenderAccountResponse = yield call(
      senderAccountService.updateSenderAccount,
      payload
    );
    yield put(updateSenderAccountSuccess(response.senderAccount));
    yield put(getDomainsRequest());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      error.response?.data?.message?.details ||
      'Failed to update sender account';
    yield put(updateSenderAccountFailure(errorMessage));
  }
}

function* getSenderAccountByIdWorker({
  payload,
}: GetSenderAccountByIdRequestAction) {
  try {
    const response: AxiosResponse<SenderAccountResponse> = yield call(
      senderAccountService.getSenderAccountById,
      payload.id
    );
    yield put(getSenderAccountByIdSuccess(response.data.senderAccount));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      error.response?.data?.message?.details ||
      'Failed to get sender account';
    yield put(getSenderAccountByIdFailure(errorMessage));
  }
}

function* deleteSenderAccountWorker({
  payload,
}: DeleteSenderAccountRequestAction) {
  try {
    yield call(senderAccountService.deleteSenderAccount, payload.id);
    yield put(deleteSenderAccountSuccess(payload.id));
    yield put(getDomainsRequest());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      error.response?.data?.message?.details;
    yield put(
      deleteSenderAccountFailure(
        errorMessage || 'Failed to delete sender account'
      )
    );
  }
}

function* getVerifiedSenderAccountsWorker() {
  try {
    const response: ISenderAccount[] = yield call(
      senderAccountService.getVerifiedSenderAccounts
    );
    yield put(getVerifiedSenderAccountsSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      error.response?.data?.message?.details ||
      'Failed to get verified sender accounts';
    yield put(getVerifiedSenderAccountsFailure(errorMessage));
  }
}

export function* senderAccountsSaga() {
  yield takeLatest(CREATE_SENDER_ACCOUNT_REQUEST, createSenderAccountWorker);
  yield takeLatest(UPDATE_SENDER_ACCOUNT_REQUEST, updateSenderAccountWorker);
  yield takeLatest(
    GET_SENDER_ACCOUNT_BY_ID_REQUEST,
    getSenderAccountByIdWorker
  );
  yield takeLatest(DELETE_SENDER_ACCOUNT_REQUEST, deleteSenderAccountWorker);
  yield takeLatest(
    GET_VERIFIED_SENDER_ACCOUNTS_REQUEST,
    getVerifiedSenderAccountsWorker
  );
}
